import {
  RequireAuth,
  useFusionAuth,
  type UserInfo,
} from "@fusionauth/react-sdk";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SettingsApplicationsOutlinedIcon from "@mui/icons-material/SettingsApplicationsOutlined";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  type GridColDef,
  type GridColumnVisibilityModel,
} from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BaseTable from "../../components/datagrid/BaseTable.tsx";
import { TableNoRowsCustomization } from "../../components/datagrid/TableNoRowsCustomization.tsx";
import { useFetch } from "../../services/hooks/useFetch";
import { apiPrefix } from "../../utils/main";

export default function ProjectTable({
  handleStartProject,
}: {
  handleStartProject: () => void;
}) {
  const theme = useTheme();
  const [includeInactive, setIncludeInactive] = useState(false);
  const { userInfo } = useFusionAuth<UserInfo>();
  const isAdmin = userInfo?.roles?.includes("ADMIN");
  const {
    data: projects,
    isLoading,
    fetch,
    status,
    reset,
  } = useFetch<Project[]>();

  useEffect(() => {
    reset();
  }, [includeInactive, reset]);

  useEffect(() => {
    if (!isLoading && status === null) {
      void fetch(`${apiPrefix}/projects?includeInactive=${includeInactive}`);
    }
  }, [fetch, includeInactive, isLoading, status]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Project Name",
      editable: false,
      flex: 1,
      minWidth: 250,
      headerClassName: "project-table-header",
      renderCell: (params) => {
        const project = params.row;
        return (
          <MuiLink
            component={Link}
            to={`/details?projectId=${project.id}`}
            variant="body2"
          >
            {project.name}
          </MuiLink>
        );
      },
    },
    {
      field: "statusName",
      headerName: "Status",
      editable: false,
      flex: 0.5,
      minWidth: 150,
      headerClassName: "project-table-header",
      renderCell: (params) => {
        const project = params.row;
        return project.isActive ? (
          <Chip label={project.statusName} size="small" />
        ) : (
          <Chip label={"Inactive"} color={"error"} size="small" />
        );
      },
      valueGetter: (_value, row) => {
        return row.isActive ? row.statusName : "Inactive";
      },
      type: "singleSelect",
      valueOptions: ["In Progress", "Inactive"],
    },
    {
      field: "deviceName",
      headerName: "Device Name",
      editable: false,
      flex: 1,
      minWidth: 250,
      headerClassName: "project-table-header",
      valueGetter: (_value, row) => {
        const descriptions = row.devices?.map(
          (device: Device) => device.description,
        );
        return descriptions?.join(" ");
      },
      renderCell: (params) => {
        const project = params.row;
        const descriptions = project.devices?.map(
          (device: Device) => device.description,
        );
        const descriptionList = descriptions?.join(", ");
        return (
          <Tooltip title={descriptionList} placement={"bottom-start"}>
            <span>{descriptionList}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "patientCount",
      headerName: "Patient Records",
      editable: false,
      flex: 0.5,
      type: "number",
      minWidth: 150,
      headerClassName: "project-table-header",
      renderCell: (params) => {
        const project = params.row;
        return (
          <Box sx={{ color: theme.palette.primary.main }}>
            {project.patientCount !== null && project.patientCount >= 10
              ? project.patientCount
              : "<10"}
          </Box>
        );
      },
    },
    {
      field: "isLocked",
      headerName: "Permissions",
      editable: false,
      flex: 0.5,
      minWidth: 150,
      headerClassName: "project-table-header",
      cellClassName: (params) =>
        params.row.isLocked ? "project-table-permissions-locked" : "",
      valueGetter: (value) => {
        return value ? "Locked" : "Unlocked";
      },
      type: "singleSelect",
      valueOptions: ["Locked", "Unlocked"],
      renderCell: (params) => {
        const project = params.row;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: "0.25rem",
            }}
          >
            {project.isLocked ? (
              <LockOutlinedIcon fontSize="small" />
            ) : (
              <LockOpenOutlinedIcon fontSize="small" />
            )}
            {isAdmin ? (
              <MuiLink
                component={Link}
                to={`/settings?projectId=${project.id}&settings=project-permissions`}
                variant="body2"
              >
                {project.isLocked ? "Locked" : "Unlocked"}
              </MuiLink>
            ) : (
              <Typography variant="body2">
                {project.isLocked ? "Locked" : "Unlocked"}
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 0.5,
      filterable: false,
      valueFormatter: (value: string) => {
        const date = new Date(value);
        return date.toDateString();
      },
    },
  ];

  if (isAdmin) {
    columns.push(
      {
        field: "latestSnapshotId",
        headerName: "Last Snapshot ID",
        flex: 0.5,
        valueGetter: (_value: undefined, row: Project) => {
          return row.currentSnapshotVersion?.id;
        },
        filterable: false,
      },
      {
        field: "latestSnapshotDate",
        headerName: "Last Snapshot Date",
        flex: 0.5,
        valueFormatter: (value: string) => {
          const date = new Date(value);
          return date.toDateString();
        },
        valueGetter: (_value: undefined, row: Project) => {
          return row.currentSnapshotVersion?.createdAt;
        },
        filterable: false,
      },
      {
        field: "latestSnapshotStatus",
        headerName: "Last Snapshot Status",
        flex: 0.5,
        valueGetter: (_value: undefined, row: Project) => {
          return row.currentSnapshotVersion?.status;
        },
        filterable: false,
      },
      {
        field: "dataSchema",
        headerName: "Data Schema",
        flex: 0.4,
        valueGetter: (_value: undefined, row: Project) => {
          return row.currentSnapshotVersion?.dataVersion;
        },
      },
    );
  }

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      name: true,
      statusName: true,
      deviceName: true,
      patientCount: true,
      locked: true,
      createdAt: false,
      latestSnapshotId: false,
      latestSnapshotDate: false,
      latestSnapshotStatus: false,
      dataSchema: false,
    });

  const CustomNoRowsOverlay = () => {
    return (
      <TableNoRowsCustomization
        icon={
          <SettingsApplicationsOutlinedIcon
            style={{
              color: "#0D3E56",
              width: "48px",
              height: "48px",
              marginLeft: "18px",
            }}
          />
        }
        text={"You have not created any projects yet."}
      >
        <RequireAuth withRole={"ADMIN"}>
          <Button variant="contained" onClick={handleStartProject}>
            Start a Project
          </Button>
        </RequireAuth>
      </TableNoRowsCustomization>
    );
  };

  const InactiveButton = (
    <RequireAuth withRole="ADMIN">
      <FormControlLabel
        className={"inactive-switch"}
        control={
          <Switch
            checked={includeInactive}
            onChange={() => setIncludeInactive(!includeInactive)}
          />
        }
        label="Show Inactive Projects"
      />
    </RequireAuth>
  );

  return (
    <Paper className="table-background">
      <BaseTable<Project>
        boxClassName={"datagrid-box"}
        loading={isLoading}
        columns={columns}
        rows={projects || []}
        columnVisibilityModel={columnVisibilityModel}
        getRowHeight={() => null}
        density="standard"
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            customElements: InactiveButton,
          },
        }}
        pageSizeOptions={[10, 20, { value: -1, label: "All" }]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        tableId={`company:${userInfo?.companyId}:project-table`}
        sx={{
          overflowX: "auto",
          border: "none",
          backgroundColor: "white",
          "& .project-table-header": {
            backgroundColor: "white",
          },
          "& .MuiDataGrid-footerContainer": {
            border: "none",
          },
        }}
      />
    </Paper>
  );
}
